<i18n lang="yaml">
ja:
  add: "追加"
  remove: "削除"
  language: "言語"
  level: "レベル"
  tag: "{lang}（{level}）"
  levels:
    native: "ネイティブ"
    professional: "ビジネスレベル"
    conversational: "会話レベル"
  fields:
    personal:
      languages: "対応言語"
</i18n>
<script setup lang="ts">
import { computed, reactive, watch } from "vue";
import { storeToRefs } from "pinia";

import { removeItem } from "@/modules/utils";

import { useI18n } from "vue-i18n";
import { use as useSession } from "@/stores/session";

import UISelect from "@/components/ui/Select.vue";

import ItemTags from "./ProfileTags.vue";

import type { UserProfile } from "@/types/profiles.d";

const { t } = useI18n();
const session = useSession();
const { agent, active } = storeToRefs(session);
const { editing, data } = defineProps<{
  editing: boolean;
  data: { personal: Partial<UserProfile["personal"]> };
}>();

const parsed = computed(() => {
  return (agent.value?.personal.languages ?? []).map((item) => {
    const [lang, level] = item.split("/");
    return { lang, level };
  });
});
const tags = computed(() => {
  return parsed.value.map(({ lang, level }) => {
    return t("tag", { lang: t("langs." + lang), level: t("levels." + level) });
  });
});

const values = reactive<
  Array<{
    lang: string | null;
    level: string | null;
  }>
>(parsed.value.length ? parsed.value : [{ lang: null, level: null }]);

watch(values, (values) => {
  data.personal.languages = values
    .filter((item) => item.lang && item.level)
    .map((item) => `${item.lang}/${item.level}`);
});

const withLabel = (prefix: string) => (value: string) => ({
  value,
  label: t(prefix + "." + value),
});

const filterLang = (current: string | null) => (lang: string) =>
  current == lang || values.every((item) => item.lang != lang);

const config = {
  levels: ["native", "professional", "conversational"],
  langs: ["ja", "en", "zh", "ko", "vi", "tgl", "pt", "fr"],
};
</script>
<template>
  <div class="field" v-if="agent">
    <h3 class="w6 t16" v-t="'fields.personal.languages'" />
    <template v-if="editing">
      <div
        class="lang u-row"
        v-for="value of values"
        :key="value.lang ?? 'null'"
      >
        <UISelect
          :options="
            config.langs.filter(filterLang(value.lang)).map(withLabel('langs'))
          "
          :placeholder="t('language')"
          :value="value.lang"
          @update:value="value.lang = $event"
        />
        <UISelect
          :options="config.levels.map(withLabel('levels'))"
          :placeholder="t('level')"
          :value="value.level"
          @update:value="value.level = $event"
        />
        <button
          type="button"
          class="cl-black-60"
          v-t="'remove'"
          @click="removeItem(values, value)"
        />
      </div>
      <button
        type="button"
        class="add btn primary outline"
        v-t="'add'"
        @click="values.push({ lang: null, level: null })"
      />
    </template>
    <ItemTags :tags="tags" v-else-if="agent.personal.languages || active" />
  </div>
</template>
<style scoped>
.ui-select {
  width: 220px;
  margin: 8px 8px 0 0;
}
.btn.add {
  margin-top: 8px;
}
</style>
